/**
 * Persist parsed agenda JSON onto a live meeting via existing group/entry APIs.
 * Each write carries the latest edit_version so OCC does not 409 mid-import.
 */
export async function persistParsedAgendaToMeeting({
  meetingMinutesId,
  meetingId,
  parsed,
  mode = "append",
  existingGroups = [],
  editVersion,
  createGroup,
  createEntry,
  deleteGroup,
}) {
  let version = editVersion;
  const withVersion = (payload) => (version ? { ...payload, edit_version: version } : payload);
  const adopt = (record) => {
    if (record?.edit_version) version = record.edit_version;
  };

  if (mode === "replace") {
    for (const group of existingGroups) {
      if (group?.id) {
        const removed = await deleteGroup(meetingMinutesId, meetingId, group.id, version);
        adopt(removed);
      }
    }
  }

  const startOrder = mode === "replace" ? 0 : existingGroups.length;
  const createdGroups = [];

  for (const [index, group] of (parsed.groups || []).entries()) {
    const newGroup = await createGroup(meetingMinutesId, meetingId, withVersion({
      title: group.title,
      description: group.description,
      order: startOrder + index,
    }));
    if (!newGroup?.id) {
      const err = new Error("create_group_failed");
      err.code = "create_group_failed";
      err.details = newGroup?.errors;
      throw err;
    }
    adopt(newGroup);

    const entries = [];
    for (const [entryIndex, entry] of (group.entries || []).entries()) {
      const newEntry = await createEntry(meetingMinutesId, meetingId, newGroup.id, withVersion({
        description: entry.description,
        deadline: entry.deadline || "",
        order: entryIndex,
      }));
      if (!newEntry?.id) {
        const err = new Error("create_entry_failed");
        err.code = "create_entry_failed";
        err.details = newEntry?.errors;
        throw err;
      }
      adopt(newEntry);
      entries.push(newEntry);
    }
    createdGroups.push({ ...newGroup, entries });
  }

  return createdGroups;
}
